import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, Receipt, Sparkles, CalendarDays, Shield, RadioTower, ChartSpline } from "lucide-react";
import { cn } from "@/lib/utils";
import { StoreFeedDot } from "@/components/dashboard/FeedAlertBanner";

export const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_fbf41b09-d279-44ec-b80d-da1d7a743c90/artifacts/84dz6r0l_Zapin-New-Logo-TM-White-2048x1749.webp";

export const NAV = [
  { to: "/", label: "Executive Summary", hint: "All stores at a glance", icon: LayoutDashboard, end: true },
  { to: "/count", label: "Count Analytics", hint: "Footfall, walk-in, who visits", icon: Users },
  { to: "/sales", label: "Sales & POS", hint: "Revenue, mix, sales vs footfall", icon: Receipt },
  { to: "/ai-growth", label: "AI Growth", hint: "Recommendations & promotions", icon: Sparkles },
  { to: "/forecast", label: "Forecast", hint: "Footfall & revenue, up to a year ahead", icon: ChartSpline },
  { to: "/calendar", label: "Calendar & Events", hint: "Holidays MY · SG · Johor", icon: CalendarDays },
  { to: "/admin/users", label: "Users", hint: "Accounts & passwords", icon: Shield, admin: true },
  { to: "/admin/live", label: "Live Count Feed", hint: "API key & integration brief", icon: RadioTower, admin: true },
];

export const Brand = ({ compact = false }) => (
  <div className="flex items-center gap-3">
    <span className="h-10 w-10 rounded-lg bg-[hsl(220_18%_10%)] border border-border/60 flex items-center justify-center shrink-0">
      <img src={LOGO} alt="Zapin" className="h-7 w-auto" data-testid="sidebar-logo" />
    </span>
    {!compact && (
      <div className="min-w-0">
        <div className="font-display text-lg leading-tight tracking-tight">Zapin</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">Travel Retail Intelligence</div>
      </div>
    )}
  </div>
);

export const NavItems = ({ isAdmin, feed, badges = {}, onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1 overflow-y-auto scroll-thin" aria-label="Main">
    {NAV.filter((n) => !n.admin || isAdmin).map((item) => {
      const Icon = item.icon;
      const badge = badges[item.to];
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.to === "/" ? "summary" : item.to.replace(/\//g, "-").replace(/^-/, "")}`}
          className={({ isActive }) =>
            cn(
              "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left focus-gold transition-colors duration-200 border-l-2",
              isActive ? "bg-muted/40 text-foreground border-l-gold" : "text-muted-foreground hover:text-foreground hover:bg-muted/25 border-l-transparent"
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon size={17} className={cn(isActive && "text-gold")} strokeWidth={1.75} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{item.label}</div>
                <div className="text-[11px] text-muted-foreground/80 truncate">{item.hint}</div>
              </div>
              {badge && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-gold/30 bg-gold/15 text-gold">{badge}</span>}
              {item.to === "/admin/live" && feed?.state === "live" && <StoreFeedDot entry={{ id: "nav", state: "live" }} />}
            </>
          )}
        </NavLink>
      );
    })}
  </nav>
);

export const Sidebar = ({ isAdmin, feed, badges, footer }) => (
  <aside data-testid="sidebar" className="print-hidden hidden lg:flex fixed inset-y-0 left-0 w-[272px] flex-col border-r border-border/60 bg-[hsl(var(--sidebar-bg))]/95 backdrop-blur-md z-20">
    <div className="px-6 pt-6 pb-5">
      <Brand />
    </div>
    <div className="px-6 pb-4">
      <div className="hairline" />
    </div>
    <NavItems isAdmin={isAdmin} feed={feed} badges={badges} />
    <div className="px-6 py-5 border-t border-border/60 text-[11px] text-muted-foreground space-y-1">{footer}</div>
  </aside>
);
